import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { Link, useHistory } from 'react-router-dom'
import CaseCard from './CaseCard'
import { XIcon } from '@heroicons/react/outline'
import getGoogle from '../../../lib/google-maps'
import Spinner from '../../../lib/components/Spinner/Spinner'
import StoreContext from '../../providers/storesContext'
import { topBarStore } from '../../../lib/stores/TopBarStore'
import HOME, { caseDetailUrl } from '../../routing/paths'
import sortByDistance from '../../../lib/util/sortByDistance'

const startingWidth = window.innerWidth
const startingMargin = (startingWidth - 312) / 2

const CaseSelfReleaseDropOff = () => {
  const mapDivRef = useRef(null)
  const { caseDetailStore } = useContext(StoreContext)
  const history = useHistory()
  const { immobilizationCase } = caseDetailStore
  const [xValue, setXValue] = useState(0);
  const [currentMargin, setCurrentMargin] = useState(startingMargin)
  const [posArray, setPosArray] = useState(0)
  const [locations, setLocations] = useState([])
  const dropOffRef = useRef([])
  const locationRef = useRef()
  const map = useRef()
  var autocomplete
  var markerSearch

  const clearInputField = () => {
    document.getElementById('autocomplete').value = ''
  }

  const initAutocomplete = () => {
    autocomplete = new google.maps.places.Autocomplete( document.getElementById('autocomplete'), {
      fields: ['place_id', 'name', 'formatted_address', 'geometry'],
      componentRestrictions: { country: 'us' }, })

    autocomplete.addListener('place_changed', onPlaceChanged)
  }

  const onPlaceChanged = ( () => {
      const place = autocomplete.getPlace()
      markerSearch.setVisible(false);

      if (!place.geometry || !place.geometry.location) {
        document.getElementById('autocomplete').placeholder = "No details available for input: '" + place.name + "'"
        return
      }

      if (place.geometry.viewport) {
        map.current.fitBounds(place.geometry.viewport)
      } else {
        map.current.setCenter(place.geometry.location)
        map.current.setZoom(17)
      }
      markerSearch.setPosition(place.geometry.location)
      locationRef.current = place.geometry.location
      const sortedCases = sortByDistance([...dropOffRef.current], {lat: locationRef.current.lat(), lng: locationRef.current.lng()})
      setLocations(sortedCases)
      dropOffRef.current = sortedCases
      setCurrentMargin(startingMargin)
      markerSearch.setVisible(true);
    })

  useEffect(() => {
    if (!immobilizationCase.isPaid) {
      history.push(caseDetailUrl(immobilizationCase))
      return
    }

    caseDetailStore.getDropOffLocations().then(() => {
      if (!caseDetailStore.dropOffLocationsError) {
        dropOffRef.current = caseDetailStore.dropOffLocations;
        setLocations(caseDetailStore.dropOffLocations)

        getGoogle().then((google) => {
          const [firstDropOffLocation] = caseDetailStore.dropOffLocations

          map.current = new google.maps.Map(mapDivRef.current, {
            center: new google.maps.LatLng(
              firstDropOffLocation.position.lat,
              firstDropOffLocation.position.lng
            ),
            zoom: 12,
            minZoom: 3,
            restriction: {
              latLngBounds: {
                north: 80,
                south: -80,
                west: -179,
                east: 179,
              },
              strictBounds: false,
            },
            disableDefaultUI: true,
          })

          initAutocomplete()

          const bounds = new google.maps.LatLngBounds()

          markerSearch = new google.maps.Marker({
            icon: '/icons/black-location-marker.svg',
            map: map.current,
          });

          caseDetailStore.dropOffLocations.map(({ position }) => {
            const marker = new google.maps.Marker({
              icon: '/icons/google-maps-marker.svg',
              position: new google.maps.LatLng(position.lat, position.lng),
              map: map.current
            })

            marker.addListener('click', () => {
              map.current.setZoom(16)
              map.current.setCenter(marker.getPosition())
            })

            const markerPosition = marker.getPosition()
            const loc = new google.maps.LatLng(marker.position.lat(), marker.position.lng())
            bounds.extend(loc)

            marker.addListener('click', () => {
              const index = getMarkerIndex(marker.getPosition())
              console.log(index)
              clickOnCard(index)
            })
          })

          map.current.fitBounds(bounds)
          map.current.panToBounds(bounds)
        })
      }

    })
  }, [])

  useEffect(() => {
    setCurrentMargin(startingMargin - 312 * posArray)
  }, [posArray])

  if (caseDetailStore.isLoadingDropOffLocations) {
    return (
      <div className="flex justify-center h-full items-center">
        <Spinner color="text-primary-light" size={8} />
      </div>
    )
  }

  const getMarkerIndex = (position) => {
    return dropOffRef.current.findIndex((dropOffLocation) => {
      return dropOffLocation.position.lat === position.lat() && dropOffLocation.position.lng === position.lng()
    })
  }

  const clickOnCard = (index) => {
    map.current.panTo(dropOffRef.current[index].position)
    map.current.setZoom(17)
    setPosArray(index)
  }

  const moveRight = () => {
    if (posArray < locations.length - 1) {
      setPosArray(posArray + 1)
    }
  }
  const moveLeft = () => {
    if (posArray > 0) {
      setPosArray(posArray - 1)
    }
  }
  const handleMouseDown = (event) => {
    setXValue(event.touches[0].clientX)
  }

  const handleMouseUp = (event) => {
    if (event.changedTouches[0].clientX > xValue + 10) {
      moveLeft()
    } else if (event.changedTouches[0].clientX < xValue - 10) {
      moveRight()
    }
  }

  return (
    <div className="fixed mt-14 w-full top-0 left-0 sm:max-h-3/4">
      <div className="mt-4 ml-2 absolute z-30 w-6/12 md:w-4/12">
        <span className="absolute flex items-center right-0 inset-y-0 text-gray-300">
          <div className="mr-1 px-2 bg-white">
            <img src="/icons/search-icon.svg" className="cursor-pointer" alt='search-icon' onClick={clearInputField} />
          </div>
        </span>
        <input id="autocomplete"
          placeholder='Search for nearby addresses...'
          type='text'
          className="w-full h-12 rounded-lg border border-gray-300 px-2 drop-shadow-lg "
          onClick={clearInputField}
        />
      </div>
      <div id="dropoff-map" className="w-full h-screen" ref={mapDivRef}/>
      <div
        onTouchEnd={(event) => handleMouseUp(event)}
        onTouchStart={(event) => handleMouseDown(event)}
        className="overflow-hidden mb-6 w-full z-20 flex flex-col fixed bottom-0"
      >
         <div className="flex space-x-3"
          style={{
                marginLeft: `${currentMargin}px`,
                transition: 'all 0.5s ease'
                }}
          >
          {locations.map((c, index) => {
            return (
              <CaseCard
                key={c.id}
                index={index}
                cardCase={c}
                location={c.position}
                onClick={clickOnCard}
                />
                    )
          })}
        </div>
      </div>
    </div>
  )
}

export default observer(CaseSelfReleaseDropOff)
